import { defineStore } from 'pinia';
import { jwtDecode } from 'jwt-decode';
import router from '@/router';
import API from '@/store/api/jwt';
import {
  Activation,
  ConfirmResetPassword,
  DecodedJWTPayload,
  EmailReset,
  JWT,
  UserLoginInfo
} from '@/store/models/jwt';
import { UserDetail } from '@/store/models/user';
import { useBlockStore } from '@/store/modules/block';
import { useCommonStore } from '@/store/modules/common';
import { LocalStorage } from 'quasar';
import { bus, type SubscribeEventPayload } from '@/utils/event-bus';

export const useJwtStore = defineStore('jwt', {
  state: () => ({
    AccessToken: '',
    DecodedPayload: {} as DecodedJWTPayload,
    isLoggedIn: false,
    RefreshingToken: true,
    UserDetail: {} as UserDetail
  }),
  getters: {
    loggedIn(state): boolean {
      return state.isLoggedIn;
    }
  },
  actions: {
    refreshingToken() {
      this.RefreshingToken = true;
    },
    async getJWT(user: UserLoginInfo) {
      const detail = (await API.getJWT(user)) as JWT;
      const decodedPayload = jwtDecode(detail.access) as DecodedJWTPayload;
      bus.emit('login');
      this.AccessToken = detail.access;
      this.DecodedPayload = decodedPayload;
      this.UserDetail = decodedPayload.userDetail;
      this.isLoggedIn = true;
    },
    async refreshAccessToken() {
      let detail = (await API.refreshAccessToken()) as JWT;
      let decodedPayload: DecodedJWTPayload = {} as DecodedJWTPayload;
      let isLoggedIn = false;
      if (!detail || !detail.access) {
        const loggedIn = this.isLoggedIn;
        detail = {} as JWT;
        detail.access = '';
        decodedPayload = {} as DecodedJWTPayload;
        if (loggedIn) {
          router.push({ name: 'Home' });
        }
      } else {
        decodedPayload = jwtDecode(detail.access);
        isLoggedIn = true;
      }
      bus.emit(isLoggedIn ? 'login' : 'logout');
      this.AccessToken = detail.access;
      this.DecodedPayload = decodedPayload;
      this.isLoggedIn = isLoggedIn;
      this.UserDetail = decodedPayload.userDetail;
      this.RefreshingToken = false;
    },
    async clearJWT() {
      bus.emit('logout');
      await API.clearJWT();
      const commonStore = useCommonStore();
      const blockStore = useBlockStore();
      commonStore.setSelectedFarmBlock({
        farmId: 0,
        name: '',
        center: [0, 0],
        extent: null
      });
      commonStore.setFilterFarmId(null);
      LocalStorage.remove('SelectedFarmBlock');
      blockStore.clearAllMyBlocksWithGeom();
      this.AccessToken = '';
      this.DecodedPayload = {} as DecodedJWTPayload;
      this.isLoggedIn = false;
    },
    async userActivation(data: Activation) {
      return await API.userActivation(data);
    },
    async resendActivation(data: EmailReset) {
      return await API.resendActivation(data);
    },
    async resetPassword(data: EmailReset) {
      return await API.resetPassword(data);
    },
    async confirmResetPassword(data: ConfirmResetPassword) {
      return await API.confirmResetPassword(data);
    },
    async subscribe(data: SubscribeEventPayload) {
      return await API.subscribe(data);
    }
  }
});

